import { useState, useEffect, useMemo } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { appId } from '../constants';
import { safeOnSnapshot } from '../utils';

let cachedRawRecommendations = null;
let cachedPlaces = null;
let fetchPromise = null;
const subscribers = new Set();

function notifySubscribers() {
  subscribers.forEach(callback => callback());
}

async function fetchRecommendationsAndPlaces() {
  if (fetchPromise) return fetchPromise;

  fetchPromise = (async () => {
    try {
      const recsQuery = query(collection(db, 'artifacts', appId, 'public', 'data', 'recommendations'));
      const placesQuery = query(collection(db, 'artifacts', appId, 'public', 'data', 'places'));

      const [recsSnapshot, placesSnapshot] = await Promise.all([
        getDocs(recsQuery),
        getDocs(placesQuery)
      ]);

      const recs = [];
      recsSnapshot.forEach(doc => {
        recs.push({ id: doc.id, ...doc.data() });
      });

      const placesMap = {};
      placesSnapshot.forEach(doc => {
        placesMap[doc.id] = doc.data();
      });

      cachedRawRecommendations = recs;
      cachedPlaces = placesMap;
    } catch (error) {
      console.error("Error fetching recommendations and places:", error);
      // Initialize to empty on error so loading stops
      cachedRawRecommendations = cachedRawRecommendations || [];
      cachedPlaces = cachedPlaces || {};
    }
  })();

  await fetchPromise;
  notifySubscribers();
  return fetchPromise;
}

export async function refreshRecommendations() {
  fetchPromise = null;
  await fetchRecommendationsAndPlaces();
}

export function useRecommendations() {
  const [localRaw, setLocalRaw] = useState(cachedRawRecommendations || []);
  const [localPlaces, setLocalPlaces] = useState(cachedPlaces || {});
  const [loading, setLoading] = useState(!cachedRawRecommendations || !cachedPlaces);
  
  const [aliases, setAliases] = useState([]);

  useEffect(() => {
    let mounted = true;
    const update = () => {
      if (mounted) {
        setLocalRaw(cachedRawRecommendations || []);
        setLocalPlaces(cachedPlaces || {});
        setLoading(!cachedRawRecommendations || !cachedPlaces);
      }
    };

    subscribers.add(update);
    update();

    if (!cachedRawRecommendations || !cachedPlaces) {
      fetchRecommendationsAndPlaces();
    }

    return () => {
      mounted = false;
      subscribers.delete(update);
    };
  }, []);

  useEffect(() => {
    let unsubscribeAliases = () => {};
    if (auth.currentUser) {
      const aliasesQuery = query(
        collection(db, 'artifacts', appId, 'public', 'data', 'aliases'),
        where('userId', '==', auth.currentUser.uid)
      );
      unsubscribeAliases = safeOnSnapshot(aliasesQuery, (snapshot) => {
        const results = [];
        snapshot.forEach(doc => {
          results.push({ id: doc.id, ...doc.data() });
        });
        setAliases(results);
      }, (error) => {
        console.error("Error fetching aliases:", error);
      });
    }
    return () => {
      unsubscribeAliases();
    };
  }, [auth.currentUser?.uid]);

  const recommendations = useMemo(() => {
    return localRaw.map(rec => {
      // Fallback to embedded fields if missing
      const placeId = rec.placeId || (rec.id && rec.id.split('_').length >= 2 ? rec.id.split('_').slice(1).join('_') : null);
      const p = localPlaces[placeId];
      if (!p) return rec;

      return {
        ...rec,
        name: p.name || rec.name || rec.placeName || rec.venueName,
        placeName: p.name || rec.placeName || rec.venueName || rec.name,
        venueName: p.name || rec.venueName || rec.placeName || rec.name,
        address: p.address || rec.address || rec.formattedAddress,
        lat: p.lat || rec.lat,
        lng: p.lng || rec.lng,
        weekdayDescriptions: p.hours || rec.weekdayDescriptions || rec.hours || []
      };
    });
  }, [localRaw, localPlaces]);

  return { recommendations, aliases, loading };
}
